import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface VideoConfig {
  src: string;
  poster?: string;
  title?: string;
  aspectRatio?: '16:9' | '9:16' | 'auto';
  metadata?: {
    description?: string;
    duration?: string;
    chapter?: string;
  };
  // Tracking context
  sourcePage?: string; // Which page/section opened the video
  sourceAction?: string; // What action triggered it (e.g., "watch_film_cta")
  // CTA behavior
  showCTAOnEnd?: boolean; // If true, shows CTA only when video ends (for short videos)
}

interface VideoPortalContextType {
  openVideo: (config: VideoConfig) => void;
  closeVideo: () => void;
  isOpen: boolean;
  currentVideo: VideoConfig | null;
}

const VideoPortalContext = createContext<VideoPortalContextType | null>(null);

export const useVideoPortal = () => {
  const context = useContext(VideoPortalContext);
  if (!context) {
    throw new Error('useVideoPortal must be used within VideoPortalProvider');
  }
  return context;
};

interface VideoPortalProviderProps {
  children: ReactNode;
}

export const VideoPortalProvider: React.FC<VideoPortalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<VideoConfig | null>(null);

  const openVideo = useCallback((config: VideoConfig) => {
    setCurrentVideo(config);
    setIsOpen(true);
  }, []);

  const closeVideo = useCallback(() => {
    setIsOpen(false);
    // Keep currentVideo for exit animation, will be cleared by portal
    setTimeout(() => {
      setCurrentVideo(null);
    }, 500); // Match animation duration
  }, []);

  return (
    <VideoPortalContext.Provider value={{ openVideo, closeVideo, isOpen, currentVideo }}>
      {children}
    </VideoPortalContext.Provider>
  );
};

export default VideoPortalContext;